import { Component, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "@environment";
import { MatDialogRef } from "@angular/material/dialog";

import { CartApiService } from "@app/core/http/cart-api.service";
import { LoginService } from "@app/core/services/login/login.service";
import { StorageService } from "@app/core/services/util/storage.service";
import { UtilityService } from "@app/core/services/util/utility.service";
import { ToastrService } from "@app/core/services/notification/toastr.service";
import { SessionStorageService } from "@app/core/services/util/session-storage.service";
import { GtmService } from '@core/services/analytics/gtm.service';
import { SelectorComponent } from "../dummy-template/selector/selector.component";
import { NgStyle, NgTemplateOutlet, DecimalPipe } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { BaseComponent } from "@app/core/components/base/base.component";

@Component({
    selector: "app-play-win-repeat",
    templateUrl: "./play-win-repeat.component.html",
    styleUrls: ["./play-win-repeat.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [MatIconModule, NgStyle, NgTemplateOutlet, SelectorComponent, DecimalPipe]
})
export class PlayWinRepeatComponent extends BaseComponent {
    DEPLOY_URL: string = environment.DEPLOY_URL;

    choices: { code: string; text: string; }[];

    isChoiceLocked: boolean;
    isLoading = true;
    isTncLoading = true;
    isBgImageLoading = true;
    isHowToPlayLoading = true;

    tnc: any;
    ctaText: string;
    bgImageUrl: string;
    selectedChoice = '';
    howToPlayImageUrl: string;
    showLottieAnimation = false;
    
    gameDetails: any = {};
    lottieUrl: string;

    constructor(
        private router: Router,
        private sanitizer: DomSanitizer,
        private dialogRef: MatDialogRef<PlayWinRepeatComponent>,
        private gtmService: GtmService,
        private loginService: LoginService,
        private toastrService: ToastrService,
        private storageService: StorageService,
        private cartApiService: CartApiService,
        private utilityService: UtilityService,
        private sessionStorage: SessionStorageService
    ) { 
        super();
        this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/confetti`;
    }

    ngOnInitBrowser(): void {
        this.fetchGameInfo();
    }

    async fetchGameInfo() {
        const gameData = this.getSessionGameData();
        await this.loginService.ensureSessionId(true);

        this.cartApiService.getGameInfo(gameData)
            .toPromise()
            .then((response: any) => {
                if (response.meta?.code === 200) {
                    this.hydrateGameInfo(response.data, true);
                } else if (response.meta?.error) {
                    this.handleApiError(response.meta.message);
                }
            }).catch((err) => {
                console.log(err);
            }); 
    }

    hydrateGameInfo(gameInfo: any = {}, logEvent: boolean, lastImageId: string = "", lastTncName: string = "") {
        const { game_details } = gameInfo;
      
        if (!game_details || !Object.keys(game_details).length) {
            this.clearSessionGameData();
            this.gtmService.logWebAnalyticsEvent("play_win_repeat", "Empty Game Details");
            return;
        }
      
        const { active_order, credit_filter, earned_credit, 
            game_details: { header_html, choices, selected_choice, image_id, tnc_name, is_locked, game_code } 
        } = gameInfo;

        this.choices = choices;
        this.isChoiceLocked = is_locked;
        this.selectedChoice = selected_choice;
        this.gameDetails = { header_html, image_id, tnc_name, active_order, credit_filter, earned_credit, game_code };
      
        // stop the loader
        this.isLoading = false;
      
        // set the session storage with latest info
        this.sessionStorage.set("game_data", {
            selected_choice,
            game_code
        });
      
        if (!lastImageId || lastImageId !== image_id) {
            this.fetchBgImage(image_id);
            this.fetchHowToPlayImage(image_id);
        }
      
        if (!lastTncName || lastTncName !== tnc_name) {
            this.isTncLoading = true;
            this.fetchtnc(tnc_name);
        }
      
        // log event when game info is hydrated for the first time
        if (logEvent) {
            const participationStatus = is_locked ? "participated" : selected_choice ? "option_selected" : "not_participated";
            const eventLabel = `Participation Status: ${participationStatus}`;
            this.gtmService.logWebAnalyticsEvent("play_win_repeat", eventLabel);
        }
      
        this.ctaText = active_order ? "Lock In" : "Order Now To Lock In";
    }
      

    fetchtnc(tncName: string) {
        this.utilityService.getTnC(tncName)
            .toPromise()
            .then((tnc: string) => {
                this.tnc = this.sanitizer.bypassSecurityTrustHtml(tnc);
                this.isTncLoading = false;
            })
            .catch((err) => console.log(err));
    }

    fetchBgImage(imageId: string) {
        this.isBgImageLoading = true;
        this.bgImageUrl = `https://assets.box8.co.in/eatclub_games/${imageId}/campaign_main_banner.webp`;
    }

    fetchHowToPlayImage(imageId: string) {
        this.isHowToPlayLoading = true;
        this.howToPlayImageUrl = `https://assets.box8.co.in/eatclub_games/${imageId}/how_to_play.webp`;
    }

    reHydrateGameInfo(response: any) {
        this.isLoading = true;
        this.hydrateGameInfo(response, false, this.gameDetails.image_id, this.gameDetails.tnc_name);
    }

    lockIn() {
        this.gtmService.logWebAnalyticsEvent("play_win_repeat", null, "CTA Text: " + this.ctaText);
        
        const isLoggedIn = this.storageService.get("session-id") && this.storageService.get("phone_no");
        if (!isLoggedIn) {
            this.closeDialog();
            return;
        }

        const tracking_id = this.gameDetails.active_order;
        const gameData = {
            ...this.getSessionGameData(),
            tracking_id
        };

        this.cartApiService.lockChoice(gameData)
            .toPromise()
            .then((response: any) => {
                if (response.meta?.code === 200) {
                    if (response.data?.game_details?.is_locked) {
                        this.reHydrateGameInfo(response.data);
                        this.showLottieAnimation = true;
                    } else {
                        if (!response.data?.game_details?.selected_choice) {
                            this.clearSessionGameData();
                        }
                        this.closeDialog();
                    }
                } else if (response.meta?.error) {
                    this.handleApiError(response.meta.message);
                }
            })
            .catch((err) => {
                console.log(err);
                this.handleApiError("Something went wrong! Please try again.");
            });
    }

    changeChoice(selected_choice: string) {
        if(selected_choice !== this.selectedChoice) {
            this.selectedChoice = selected_choice;
    
            const gameData = this.getSessionGameData();
            gameData.selected_choice = selected_choice;
            this.sessionStorage.set("game_data", gameData);
            this.gtmService.logWebAnalyticsEvent("play_win_repeat", null, "Choice Selected: " + selected_choice);
        }
    }

    navigateToCredit() {
        this.gtmService.logWebAnalyticsEvent("play_win_repeat", null, "Credits Earned Strip");
        this.closeDialog(); 
        this.router.navigateByUrl("/credits", {
            state: { filter: this.gameDetails.credit_filter }
        });
    }

    handleApiError(errorMessage: string) {
        this.clearSessionGameData();
        this.toastrService.openSnackBar(errorMessage, "OKAY");
    }

    getSessionGameData() {
        return this.sessionStorage.get("game_data") || {
            selected_choice: "",
            game_code: ""
        };
    }

    clearSessionGameData() {
        this.sessionStorage.set("game_data", {
            selected_choice: "",
            game_code: ""
        });
    }

    closeDialog() {
        this.dialogRef.close();
    }
}

